import { Box, Stack, styled, useTheme } from '@mui/material'
import { ResponsivePie } from '@nivo/pie'
import { decimalToLocalizedString, EmptyState, formatNumberWithAbbreviation, Percentage, tokens, useColors } from 'ComponentLibrary'
import { sumBy } from 'lodash'
import { DataSplitTooltip } from './DataSplitTooltip'
import { Typography } from 'ComponentLibrary/src/piazza/text/Typography/Typography'
import React, { useId } from 'react'
import { nivoTooltipWrapperTheme } from 'ComponentLibrary/src/common/nivoTheme'

export const leftpadWithNonBreakingSpaces = (text: string, wantedLength: number) => {
    const textLength = text.length
    const numberOfSpacesToAdd = wantedLength - textLength
    if (numberOfSpacesToAdd < 0) {
        return text
    }
    const spaces = Array(numberOfSpacesToAdd).fill('\xa0').join('')
    return spaces + text
}

export type UsageData = {
    type: string
    label?: string
    value: number
}

export type DataSplitProps = {
    data: UsageData[]
    title: string
    size?: 'small' | 'medium' | 'large'
    percentageLabel?: string
    valueLabel?: string
    emptyStateTitle?: string
    remainingLegendLabel?: string
    locale?: string
}

const DEFAULT_REMAINING_LEGEND_LABEL = 'Restliche'

type LegendLabel = {
    id: string
    color: string
    percentage: string
    valueLabel: string
    label: string
}

type LegendLabelsProps = {
    legends: LegendLabel[]
    isPrimaryPosition?: boolean
    useOnlyOneColumn?: boolean
}

export const DataLegendLabels = ({ legends, isPrimaryPosition, useOnlyOneColumn }: LegendLabelsProps) => {
    const theme = useTheme()
    return (
        <Stack direction="column" gap={tokens.SizingSpacing100}>
            {legends.map(({ id, label, color, percentage, valueLabel }, index) => {
                const shouldDisplay = isPrimaryPosition ? useOnlyOneColumn || index % 2 === 0 : index % 2 !== 0
                return (
                    shouldDisplay && (
                        <Stack key={id} direction="column" width="100%" gap={tokens.SizingSpacing050}>
                            <Typography variant="textS" color={theme.palette.customtext.black54}>
                                {label}
                            </Typography>
                            <Stack
                                direction="row"
                                width="100%"
                                gap={tokens.SizingSpacing050}
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                                    <Dot color={color} />
                                    <Typography variant="headline4" color={theme.palette.customtext.black80}>
                                        {leftpadWithNonBreakingSpaces(percentage, 2)}
                                    </Typography>
                                </Stack>
                                <Typography variant="headline4" color={theme.palette.customtext.black54} noWrap>
                                    {valueLabel}
                                </Typography>
                            </Stack>
                        </Stack>
                    )
                )
            })}
        </Stack>
    )
}

const Dot = styled('div')(({ color }: { color: string }) => {
    const isRainbow = color === 'rainbow'
    return {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: isRainbow
            ? `linear-gradient(
            180deg,
            rgba(255, 0, 0, 1) 0%,
            rgba(255, 154, 0, 1) 10%,
            rgba(208, 222, 33, 1) 20%,
            rgba(79, 220, 74, 1) 30%,
            rgba(63, 218, 216, 1) 40%,
            rgba(47, 201, 226, 1) 50%,
            rgba(28, 127, 238, 1) 60%,
            rgba(95, 21, 242, 1) 70%,
            rgba(186, 12, 248, 1) 80%,
            rgba(251, 7, 217, 1) 90%,
            rgba(255, 0, 0, 1) 100%
        )`
            : color,
    }
})

const DEFAULT_EMPTY_STATE_TITLE = 'Es stehen noch nicht genügend Daten zur Verfügung'

const DataSplit = ({
    data,
    title,
    percentageLabel,
    valueLabel,
    size = 'small',
    emptyStateTitle = DEFAULT_EMPTY_STATE_TITLE,
    remainingLegendLabel = DEFAULT_REMAINING_LEGEND_LABEL,
    locale = 'de-DE',
}: DataSplitProps) => {
    const { getColor } = useColors()
    data = data.sort((a, b) => b.value - a.value)
    // I hate doing this, but it's the fastest way to get the tooltip to show the correct value :grimacing:
    const coloredData = data.map((d, index) => ({
        ...d,
        id: d.type,
        label: d.label ? d.label : d.type,
        color: getColor(index),
    }))
    const total = sumBy(data, 'value')
    let legends = coloredData
        .map(({ value, id, label, color }) => {
            const percentage = total ? new Percentage(value / total).format({ locale }).replace(' ', '') : '100%'
            // To ensure symbols align always abbreviate numbers
            const valueWithAbbreviation = formatNumberWithAbbreviation(value, 1, true, undefined, locale)
            // Abbreviation with space is of length 4, max value unabbreviated is 9999, euro sign adds two characters therefore pad to 4+4+2=10
            const valueLabel = leftpadWithNonBreakingSpaces(`${valueWithAbbreviation} €`, 10)
            return { id, percentage, label, color, value, valueLabel }
        })
        .filter((item) => item.value > 0)

    const useOnlyOneColumn = legends.length < 4

    if (legends.length > 6) {
        const topFiveLegends = legends.slice(0, 5)
        const sumOfOtherLegends = legends.slice(5).reduce((acc, legend) => acc + legend.value, 0)
        const sonstigeLegend = {
            id: 'sonstige',
            percentage: new Percentage(sumOfOtherLegends / total).format({ locale }).replace(' ', ''),
            label: remainingLegendLabel,
            color: 'rainbow',
            value: sumOfOtherLegends,
            valueLabel: leftpadWithNonBreakingSpaces(
                `${formatNumberWithAbbreviation(sumOfOtherLegends, 1, true, undefined, locale)} €`,
                10
            ),
        }
        legends = [...topFiveLegends, sonstigeLegend]
    }

    const sizeOptions = {
        small: 120,
        medium: 158,
        large: 178,
    }

    const labelId = useId()

    return (
        <Stack height="100%" width="100%" direction="column" gap={tokens.SizingSpacing200}>
            <Typography variant="headline3" color={tokens.ColorTextBoldest} id={labelId}>
                {title}
            </Typography>
            {data.length > 0 ? (
                <Stack direction="row" gap={tokens.SizingSpacing200} alignItems="center" justifyContent="space-evenly">
                    <LegendLabelsFlexContainer>
                        <DataLegendLabels legends={legends} isPrimaryPosition useOnlyOneColumn={useOnlyOneColumn} />
                    </LegendLabelsFlexContainer>
                    <Box height={sizeOptions[size]} width={sizeOptions[size]} aria-labelledby={labelId}>
                        <ResponsivePie
                            data={coloredData}
                            margin={{ top: 4, right: 8, bottom: 4, left: 8 }}
                            innerRadius={0.75}
                            padAngle={2}
                            cornerRadius={2}
                            activeOuterRadiusOffset={2}
                            borderWidth={0}
                            colors={{ datum: 'data.color' }}
                            layers={['arcs']}
                            theme={nivoTooltipWrapperTheme}
                            valueFormat={(value) => `${decimalToLocalizedString(value, false, 2, undefined, locale)} €`}
                            tooltip={({ datum }) => (
                                <DataSplitTooltip
                                    datum={datum}
                                    total={total}
                                    percentageLabel={percentageLabel}
                                    valueLabel={valueLabel}
                                    locale={locale}
                                />
                            )}
                        />
                    </Box>
                    {!useOnlyOneColumn && (
                        <LegendLabelsFlexContainer>
                            <DataLegendLabels legends={legends} />
                        </LegendLabelsFlexContainer>
                    )}
                </Stack>
            ) : (
                <EmptyState title={emptyStateTitle} size="medium" textColor={tokens.ColorTextDefault} />
            )}
        </Stack>
    )
}

const LegendLabelsFlexContainer = styled('div')(() => ({
    alignContent: 'center',
    display: 'flex',
    justifyContent: 'center',
    minWidth: 0,
}))

export default DataSplit
